<script setup lang="ts">
/**
 * The Activity Center trigger — https://app.notion.com/p/3c304e9a59288185892ef46b9483144f §3.
 *
 * Lives at the right edge of the global app bar (`AppBar.vue`), the one piece of chrome present on
 * every app route, so long-running work is reachable from chat, the library or settings alike. That
 * reachability is the entire reason this feature exists: a quota block reported only in the documents
 * column was invisible to a user working in chat.
 *
 * **It has a resting state, on purpose.** The first build rendered nothing when idle ("huella cero"),
 * which made sense while the trigger sat inside the rail's vertical list. In a horizontal bar, a
 * control that appears and disappears reflows the right edge every time a job starts — so idle is a
 * quiet bell with no border, text or dot, and the position is stable. §3 of the feature doc records
 * the reversal.
 */
import { Bell, Loader2, CircleSlash } from 'lucide-vue-next'
import { useActivityStore } from '~/stores/activity'
import { useActivityRunsStore } from '~/stores/activityRuns'

/**
 * `labelled` is the caller telling us there is room for the sentence. The bar reserves exactly the
 * context rail's width for this control and clips it, so with the rail collapsed (48px) the pill
 * would be cut mid-word — the label is the first thing to go, not the last. The icon, the dot and the
 * title/aria-label still carry the state at any width.
 */
/**
 * `mobile` marks the copy in the phone header (`AppBar.vue`, MAR-1350). It omits `data-activity-trigger`
 * and carries `data-activity-trigger-mobile` instead: the outside-click guard that hook serves belongs
 * to the desktop dropdown (the phone panel is a full-screen sheet with no outside), and a second
 * element with the hook would make every `[data-activity-trigger]` lookup ambiguous.
 */
const { labelled = true, mobile = false } = defineProps<{ labelled?: boolean, mobile?: boolean }>()

const { t } = useI18n()
const activity = useActivityStore()
const activityRuns = useActivityRunsStore()

/** The pill treatment only earns its border when the label is actually in it. */
const expanded = computed(() => activity.hasWork && labelled)

/** The label answers a question rather than showing a bare icon (§5). */
const label = computed(() => {
  const { tone, count } = activity.summary
  if (tone === 'attention') return t('activity.summaryAttention', count)
  if (tone === 'working') return t('activity.summaryWorking', count)
  return t('activity.summaryDone', count)
})
/**
 * Opening the panel asks the SHARED feed for a tick (MAR-487, #70).
 *
 * A HANDLER, not a watcher on `activity.open` — the panel is mounted for the whole session and only
 * its contents are `v-if`'d, so `onMounted` never fires on open, and this project does not add new
 * watchers when a user gesture is available. The gesture IS the trigger.
 *
 * It used to call `toolRuns.track()`, which read a second endpoint on its own 4s timer. Now it goes
 * through `useActivityFeed`, which fetches once and arms the one interval only if that read found
 * work — so closing the panel has nothing to stop: the feed disarms itself when nothing is pending,
 * and while something IS pending the bell must keep counting it whether the panel is open or not.
 */
/**
 * `data-activity-trigger` on the button below is not decoration: `ActivityPanel`'s `onClickOutside`
 * ignores it (#84). Without that, the panel's capture-phase outside-click handler would close the
 * panel a moment before this toggle reopened it, and the bell would never appear to close anything.
 */
function onToggle() {
  const wasOpen = activity.open
  activity.toggle()
  if (!wasOpen) activityRuns.ensurePoll()
}

</script>

<template>
  <button
    type="button"
    :data-activity-trigger="mobile ? undefined : ''"
    :data-activity-trigger-mobile="mobile ? '' : undefined"
    class="relative flex min-w-0 items-center gap-1.5 h-7 rounded-lg transition-colors"
    :class="expanded
      ? 'px-2 border border-sidebar-border bg-sidebar-foreground/5 text-sidebar-foreground hover:bg-sidebar-hover'
      : 'w-8 justify-center text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-foreground'"
    :title="expanded ? undefined : (activity.hasWork ? label : t('activity.title'))"
    :aria-label="activity.hasWork ? label : t('activity.title')"
    :aria-expanded="activity.open"
    @click="onToggle()"
  >
    <span class="relative shrink-0 flex items-center">
      <Loader2 v-if="activity.summary.tone === 'working'" class="h-4 w-4 animate-spin" />
      <CircleSlash v-else-if="activity.summary.tone === 'attention'" class="h-4 w-4 text-notification" />
      <Bell v-else class="h-4 w-4" />

      <!-- The dot carries the urgency at a glance, before the sentence is read. It is the
           `notification` colour (the Slack theme's "Notifications" slot, MAR-1316): the Clear Reason
           `warning` it used to be is a pale container, invisible on the light frame. -->
      <span
        v-if="activity.attention.length"
        class="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-notification ring-2 ring-sidebar"
      />
    </span>

    <!-- Hidden on narrow viewports: the bar has one row and the breadcrumb trail needs it more. -->
    <span v-if="expanded" class="hidden md:inline text-[11px] leading-none truncate">
      {{ label }}
    </span>
  </button>
</template>
